import React, { useState } from 'react';
import { connect } from 'react-redux';
import { ToolbarItem } from '@amzn/aac-chrome-internal-api';
import { Icon, ThemeProvider } from '@amzn/storm-ui';
import { times } from '@amzn/storm-ui-icons';
import { RootState } from 'src/app/store';
import {
  selectLogoImage,
  selectToolbarItems,
  selectUserInfoFromNav,
  ToolbarItemProps,
} from 'src/features/nav/store/slice';
import { toggleMobileSideNav } from 'src/app/slice';
import {
  MobileDivider,
  MobileLogo,
  MobileMenuCloseBtn,
  MobileNavHeaderWrapper,
  MobileNavWrapper,
} from './styles';
import MobileSideNavItem from './MobileSideNavItem';
import UserPreferenceView from './UserPreferenceView';
import { ImageProps } from 'src/commons/utils/types';
import { UserInfo } from 'src/features/header/store/slice';
import { UserDropdownPublisher } from 'src/commons/utils/metrics';
import { MobileNavSheet } from './MobileNavSheet';
import {
  isStandardPageLayoutType,
  overrideModalZIndex,
} from 'src/commons/utils/helpers';
import {
  MOBILE_MENU_SUB_MENU_Z_INDEX,
  staticImgSrc,
  staticImgSrcWithCache,
} from 'src/commons/constants';
import { UserPreferenceMenuItem } from './UserPreferenceMenuItem';
import { useSidenavBundle } from 'src/providers/PantherLocalizationProvider';
import { isFromBtswWc, isFromTrim } from 'src/commons/utils';
import { useChromeConfigContext } from 'src/providers/ChromeConfigContextProvider';

interface MobileSideNavProps {
  logoImage: ImageProps;
  toolbarItems: Array<ToolbarItem>;
  userInfo: UserInfo;
  mobileMenuCloseBtnHandler: (...args: any[]) => any;
}

export const MobileSideNav: React.VFC<MobileSideNavProps> = ({
  logoImage,
  toolbarItems,
  userInfo,
  mobileMenuCloseBtnHandler,
}) => {
  const chromeConfig = useChromeConfigContext();
  const isStandardLayout = isStandardPageLayoutType(
    chromeConfig.pageLayoutType,
  );
  const bundle = useSidenavBundle();
  const [isUserPreferenceOpen, setIsUserPreferenceOpen] = useState(false);

  const [logoImageSrc, setLogoImageSrc] = useState(
    isFromTrim() || isFromBtswWc()
      ? staticImgSrcWithCache
      : logoImage?.src ?? staticImgSrcWithCache,
  );
  const handleError = (e) => {
    e.stopPropagation();
    setLogoImageSrc(staticImgSrc);
  };

  const sideNavItems = toolbarItems.map((item) => (
    <MobileSideNavItem key={item.id} {...item} />
  ));

  const firstAccessibleApp = (toolbarItems[0] as ToolbarItemProps)
    ?.sidebarGroups[0]?.links[0]?.link?.url;

  const handleUserPreferencesClick = () => {
    UserDropdownPublisher.publishCounterMonitor('Open', 1);
    setIsUserPreferenceOpen(true);
  };

  return (
    <MobileNavWrapper>
      <MobileNavHeaderWrapper>
        <MobileMenuCloseBtn
          type="button"
          onClick={mobileMenuCloseBtnHandler}
          aria-label={bundle.getMessage('sidenav-close-menu-aria-label')}
          data-ccx-e2e-id="aac-mobile-sidenav-close-btn"
        >
          <Icon type={times} size="lg" />
        </MobileMenuCloseBtn>
        <a href={firstAccessibleApp}>
          <MobileLogo
            src={logoImageSrc}
            alt={bundle.getMessage('sidenav-logo-text')}
            onError={handleError}
          />
        </a>
      </MobileNavHeaderWrapper>
      {isStandardLayout && sideNavItems}
      <>
        <MobileDivider />
        <UserPreferenceMenuItem handleClick={handleUserPreferencesClick} />
      </>

      <ThemeProvider theme={overrideModalZIndex(MOBILE_MENU_SUB_MENU_Z_INDEX)}>
        <MobileNavSheet
          isOpen={isUserPreferenceOpen}
          closeButton={{
            type: 'icon',
            label: 'close',
          }}
          type="light"
          onClose={() => {
            UserDropdownPublisher.publishCounterMonitor('Close', 1);
            setIsUserPreferenceOpen(false);
          }}
          side="start"
          padding="none"
          hasInnerPadding={false}
        >
          <UserPreferenceView {...userInfo} />
        </MobileNavSheet>
      </ThemeProvider>
    </MobileNavWrapper>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    logoImage: selectLogoImage(state),
    toolbarItems: selectToolbarItems(state),
    userInfo: selectUserInfoFromNav(state),
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    mobileMenuCloseBtnHandler: () => dispatch(toggleMobileSideNav()),
  };
};

export const ConnectedMobileSideNav = connect(
  mapStateToProps,
  mapDispatchToProps,
)(MobileSideNav);
